"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheForcePrograms() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const destinations = [{
    index: "01",
    code: "WL",
    name: "Weight Loss",
    descriptor: "Engineered caloric deficits for maximum fat oxidation.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_weight_loss.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_weight_loss.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_weight_loss.png-wvc-sizes",
    alt: "High intensity interval training session in a dark gym",
    span: "lg:col-span-7 lg:row-span-2",
    aspect: "aspect-[4/5] lg:aspect-auto lg:min-h-[640px]"
  }, {
    index: "02",
    code: "MB",
    name: "Muscle Building",
    descriptor: "Hypertrophy protocols designed for structural growth.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_muscle_building.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_muscle_building.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_muscle_building.png-wvc-sizes",
    alt: "Heavy weightlifting equipment in a professional facility",
    span: "lg:col-span-5",
    aspect: "aspect-[4/3]"
  }, {
    index: "03",
    code: "OC",
    name: "Online Coaching",
    descriptor: "Precision programming delivered to your device.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_online_coaching.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_online_coaching.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_online_coaching.png-wvc-sizes",
    alt: "Digital interface showing fitness tracking metrics",
    span: "lg:col-span-5",
    aspect: "aspect-[4/3]"
  }, {
    index: "04",
    code: "HI",
    name: "High Intensity",
    descriptor: "Explosive movements for peak athletic output.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_high_intensity.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_high_intensity.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_high_intensity.png-wvc-sizes",
    alt: "Athlete performing explosive box jumps",
    span: "lg:col-span-4",
    aspect: "aspect-[4/5]"
  }, {
    index: "05",
    code: "ST",
    name: "Strength",
    descriptor: "Foundational power through mechanical loading.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_strength.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_strength.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_strength.png-wvc-sizes",
    alt: "Barbell training with heavy plates",
    span: "lg:col-span-3",
    aspect: "aspect-[3/5]"
  }, {
    index: "06",
    code: "EN",
    name: "Endurance",
    descriptor: "Building cardiovascular capacity for longevity.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_endurance.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_endurance.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700224/img/fitness_endurance.webp-wvc-sizes",
    alt: "Athlete running on a track at dawn",
    span: "lg:col-span-5",
    aspect: "aspect-[4/3] lg:aspect-[5/4]"
  }];
  return <section data-section-id="517" id="the-force-programs" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            
            <h2 className="font-default font-bold text-foreground text-[clamp(2.5rem,6vw,4.5rem)] leading-[1.02] tracking-tighter">
              Engineered results.
              <br />
              <span className="italic font-normal text-primary">Peak performance.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">
              At Peak Force Fitness, we treat training as high-stakes engineering. 
              Our protocols are designed to strip away the unnecessary and focus 
              on the mechanical inputs that drive human transformation.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 auto-rows-min gap-4 md:gap-5 transition-opacity duration-1000 ${gridVisible ? "opacity-100" : "opacity-0"}`}>
          {destinations.map((dest, i) => <Link key={dest.code} to="/JoinTheForcePage" data-index={i} className={`group relative block overflow-hidden rounded-lg bg-card shadow-lg hover:shadow-xl transition-all duration-700 ${dest.span} ${dest.aspect}`} style={{
          transitionDelay: gridVisible ? `${i * 100}ms` : "0ms",
          transform: gridVisible ? "translateY(0)" : "translateY(24px)",
          opacity: gridVisible ? 1 : 0
        }} aria-label={`Explore ${dest.name}`}>
              {}
              <img src={dest.image} data-wvc-srcset={dest.srcset} data-wvc-sizes={dest.sizes} alt={dest.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-105" />

              {}
              <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-background/95 via-background/50 to-transparent" aria-hidden="true" />

              {}
              {i === 0 && <div className="absolute top-6 right-6 md:top-8 md:right-8 flex items-center gap-3">
                  <span className="h-px w-10 bg-primary" aria-hidden="true" />
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-primary">
                    Elite
                  </span>
                </div>}

              {}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 lg:p-10 flex items-end justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-primary mb-3">
                    {dest.index} / {dest.code}
                  </div>
                  <h3 className="font-default font-bold text-foreground text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.05] tracking-tighter">
                    {dest.name}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed max-w-sm">
                    {dest.descriptor}
                  </p>
                </div>

                {}
                <div className="shrink-0">
                  <span className="flex h-11 w-11 md:h-12 md:w-12 items-center justify-center rounded-lg border border-primary bg-primary/10 backdrop-blur-sm text-primary transition-all duration-500 group-hover:bg-primary group-hover:text-background group-hover:rotate-45 group-hover:border-primary" aria-hidden="true">
                    <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </Link>)}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-mono text-xs tracking-[0.15em] uppercase text-muted-foreground">
            Start your transformation — build the machine
          </p>
          <Link to="/JoinTheForcePage" className="group inline-flex items-center gap-3 font-default font-bold italic text-lg text-foreground hover:text-primary transition-colors duration-300">
            <span className="border-b border-primary/40 group-hover:border-primary pb-0.5">
              Start your journey
            </span>
            <ArrowUpRight className="h-5 w-5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>;
}